import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpResponse } from "@angular/common/http";
import { Observable } from "rxjs";

import { API_BASE_URL } from "../../core/api-base-url";

import IUserSkill from "@skills-app/models/userSkills";
import IConsultantSearch from "@skills-app/models/consultant-search";
import IOrgNode from "@skills-app/models/orgNode";

type HttpOptions = {
  headers: HttpHeaders;
};

@Injectable({ providedIn: "root" })
export class ConsultantSearchService {
  /**
   * API_BASE_URL is "/api" in this repo.
   */
  private readonly baseUrl = API_BASE_URL;

  private readonly skillNodesEndpoint = `${this.baseUrl}/SkillNodes`;
  private readonly rolesEndpoint = `${this.baseUrl}/UserSkills/Roles`;
  private readonly locationsEndpoint = `${this.baseUrl}/UserSkills/Locations`;

  /**
   * IMPORTANT:
   * The org tree comes from OrganisationsController, not UserSkillsController.
   * Backend route:
   *   GET /api/Organisations/all
   */
  private readonly orgsEndpoint = `${this.baseUrl}/Organisations/all`;

  private readonly options: HttpOptions = {
    headers: new HttpHeaders({ "Content-Type": "application/json" }),
  };

  constructor(private readonly http: HttpClient) {}

  /**
   * Backend: POST /api/SkillNodes/Filter
   */
  public getFilteredConsultants(
    filterConditions: IConsultantSearch,
  ): Observable<IUserSkill[]> {
    return this.http.post<IUserSkill[]>(
      `${this.skillNodesEndpoint}/Filter`,
      filterConditions,
      {
        ...this.options,
        withCredentials: true,
      },
    );
  }

  /**
   * Backend: POST /api/SkillNodes/GenerateCSV?includeSkills=true|false
   *
   * NOTE:
   * Calling this endpoint via HttpClient will NOT automatically trigger a browser download,
   * even though the backend returns Content-Disposition: attachment.
   * The caller must save the Blob explicitly (NgRx effect does this).
   */
  public createCSVFromFilter(
    filterConditions: IConsultantSearch,
    includeSkills: boolean,
  ): Observable<HttpResponse<Blob>> {
    return this.http.post(
      `${this.skillNodesEndpoint}/GenerateCSV?includeSkills=${encodeURIComponent(
        String(includeSkills),
      )}`,
      filterConditions,
      {
        headers: new HttpHeaders({ "Content-Type": "application/json" }),
        withCredentials: true,
        observe: "response",
        responseType: "blob",
      },
    );
  }

  /**
   * Backend: POST /api/UserSkills/Roles
   * Body required; [] returns "all".
   */
  public getRoles(selectedSquads: string[] = []): Observable<string[]> {
    return this.http.post<string[]>(this.rolesEndpoint, selectedSquads ?? [], {
      ...this.options,
      withCredentials: true,
    });
  }

  /**
   * Backend: POST /api/UserSkills/Locations
   * Body required; [] returns "all".
   */
  public getLocations(selectedSquads: string[] = []): Observable<string[]> {
    return this.http.post<string[]>(
      this.locationsEndpoint,
      selectedSquads ?? [],
      {
        ...this.options,
        withCredentials: true,
      },
    );
  }

  /**
   * Backend: GET /api/Organisations/all
   *
   * Backend returns ExpandedOrganisationViewModel[] which is already tree-shaped:
   *  - Id: string
   *  - Name: string
   *  - Hierarchy: string
   *  - Children: HierarchyDTO[]
   *
   * The effect layer already accepts tree-shaped org data via orgsToTree().
   */
  public getOrgs(): Observable<IOrgNode[]> {
    return this.http.get<IOrgNode[]>(this.orgsEndpoint, {
      ...this.options,
      withCredentials: true,
    });
  }
}
